import { useParkingAreaSearchListQuery } from '@kinetic-ui/data-layer';
import {
  AppSearchIconKey,
  AppSearchItemType,
  DEFAULT_ZOOM,
  MapContext,
  MapSearchResultItem,
  MapSearchResultProps,
  publicUrl,
  useAppSearchHistory,
  useGetNodes,
  useKineticMap,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
} from '@kinetic-ui/shared';
import { LocalParking } from '@mui/icons-material';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { useCallback, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/const';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { searchText } = useMapBoxSearchContext();
  const { push } = useHistory();
  const { addSearchHistory } = useAppSearchHistory();
  const map = useKineticMap();

  const { data } = useParkingAreaSearchListQuery({
    variables: { searchText, limit: 5 },
    fetchPolicy: 'cache-and-network',
  });

  const parkingAreas = useGetNodes(data?.parkingAreas);

  const onClick = useCallback(
    (id: string, geometry?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const parkingArea = parkingAreas.find((e) => e.id === id);
        if (parkingArea?.name) {
          addSearchHistory({
            name: parkingArea.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Parking,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin(geometry);
        return;
      }
      !selectedLayers.includes('parking') && setSelectedLayers([...selectedLayers, 'parking']);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [
      addMapPin,
      addSearchHistory,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      parkingAreas,
      push,
      selectedLayers,
      setSelectedLayers,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {parkingAreas.map(({ id, name, location }) => (
        <MapSearchResultItem
          onClick={(openInNewWindow) => onClick(id, location, openInNewWindow)}
          key={`searchItem_${id}`}
          icon={<LocalParking />}
          name={name}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
